import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export type ContactFormMode = 'quiz' | 'callback' | null;

interface ContactFormContextValue {
  isOpen: boolean;
  mode: ContactFormMode;
  courseName?: string;
  instanceId: number;
  /** Opens the free course-matching quiz */
  open: (courseName?: string) => void;
  /** Opens a short callback request form */
  openCallback: (courseName?: string) => void;
  close: () => void;
}

interface BuyCourseContextValue {
  isBuyOpen: boolean;
  buyCourseName?: string;
  buyInstanceId: number;
  openBuy: (courseName?: string) => void;
  closeBuy: () => void;
}

type AppModalsContextValue = ContactFormContextValue & BuyCourseContextValue;

const ContactFormContext = createContext<AppModalsContextValue | null>(null);

export const ContactFormProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<ContactFormMode>(null);
  const [courseName, setCourseName] = useState<string | undefined>();
  const [instanceId, setInstanceId] = useState(0);

  const [isBuyOpen, setIsBuyOpen] = useState(false);
  const [buyCourseName, setBuyCourseName] = useState<string | undefined>();
  const [buyInstanceId, setBuyInstanceId] = useState(0);

  const value = useMemo<AppModalsContextValue>(
    () => ({
      isOpen,
      mode,
      courseName,
      instanceId,
      open: (name?: string) => {
        setIsBuyOpen(false);
        setCourseName(name);
        setMode('quiz');
        setInstanceId((id) => id + 1);
        setIsOpen(true);
      },
      openCallback: (name?: string) => {
        setIsBuyOpen(false);
        setCourseName(name);
        setMode('callback');
        setInstanceId((id) => id + 1);
        setIsOpen(true);
      },
      close: () => {
        setIsOpen(false);
        setMode(null);
      },
      isBuyOpen,
      buyCourseName,
      buyInstanceId,
      openBuy: (name?: string) => {
        setIsOpen(false);
        setMode(null);
        setBuyCourseName(name);
        setBuyInstanceId((id) => id + 1);
        setIsBuyOpen(true);
      },
      closeBuy: () => setIsBuyOpen(false),
    }),
    [isOpen, mode, courseName, instanceId, isBuyOpen, buyCourseName, buyInstanceId]
  );

  return (
    <ContactFormContext.Provider value={value}>
      {children}
    </ContactFormContext.Provider>
  );
};

export const useContactForm = () => {
  const context = useContext(ContactFormContext);
  if (!context) {
    throw new Error('useContactForm must be used within ContactFormProvider');
  }
  return context;
};
